<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { arrowForwardOutline, checkmarkCircle, closeCircle } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import router from '@/router';

import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import TopAlert from '@/shared/components/ui/TopAlert.vue';
import useTenant from '@/shared/composables/useTenant';
import useWallet from '@/modules/wallet/composables/useWallet';

const { t } = useI18n();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { getPublicOrder } = useWallet();
const orderId = router.currentRoute.value.params.orderId?.toString() || '';
const orderMapped = ref<any>(null);
const pendingPurchase = ref<any>(null);
const loading = ref(true);
let pollingInterval: ReturnType<typeof setInterval> | null = null;

const loadOrder = async () => {
	const order = await getPublicOrder(orderId);
	pendingPurchase.value = order.pending_purchase || null;
	orderMapped.value = mapPublicOrderToTransaction(order, t);
	loading.value = false;

	return pendingPurchase.value?.status;
};

onMounted(async () => {
	const status = await loadOrder();
	if (['payment_pending', 'processing'].includes(status)) {
		pollingInterval = setInterval(async () => {
			const nextStatus = await loadOrder();
			if (!['payment_pending', 'processing'].includes(nextStatus) && pollingInterval) {
				clearInterval(pollingInterval);
				pollingInterval = null;
			}
		}, 2000);
	}
});

onUnmounted(() => {
	if (pollingInterval) {
		clearInterval(pollingInterval);
	}
});

const isProcessing = computed(() => ['payment_pending', 'processing'].includes(pendingPurchase.value?.status));
const isFailed = computed(() => ['purchase_failed', 'top_up_failed'].includes(pendingPurchase.value?.status));
const failureSubtitle = computed(() => (pendingPurchase.value?.wallet_credit_retained ? 'transaction.booking_failed.wallet_credit' : 'transaction.booking_failed.subtitle'));

const numberValue = (value: any): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const orderCurrency = (data: any) => {
	return (
		data?.overview?.currency?.[0] || {
			name: 'EUR',
			prefix: '€',
			exchange_rate: 1,
		}
	);
};

const mapCompletedPurchaseOrderToTransaction = (data: any, t: any) => {
	let totalSum = 0;
	let adminFeeAmount = 0;
	const items = data.overview.items.map((item: any) => {
		const currency = item.currency || orderCurrency(data);
		const exchangeRate = numberValue(currency.exchange_rate) || 1;
		const quantity = numberValue(item.quantity) || 1;
		const unitPrice = numberValue(item.price);
		const itemPrice = unitPrice * quantity;

		totalSum += itemPrice * exchangeRate;

		return {
			description: item.label || t('checkout.item'),
			itemPrice,
			unitPrice,
			currency: currency.prefix || '€',
			quantity,
		};
	});

	adminFeeAmount = Math.max(numberValue(data.app_service_cost), numberValue(data.funding_app_service_cost));
	if (adminFeeAmount > 0) {
		totalSum += adminFeeAmount;
		items.push({
			description: t('transaction.administrative_fee'),
			itemPrice: adminFeeAmount,
			currency: '€',
			quantity: 1,
		});
	}

	return {
		items,
		showTotalPriceString: true,
		totalPriceNumber: totalSum,
		totalDescription: t('transaction.total'),
		totalPriceWithoutFee: totalSum - adminFeeAmount,
		subtitle: t('checkout.transaction.generic.subtitle'),
		title: `€ ${totalSum.toFixed(2)}`,
		totalPriceString: `€${totalSum.toFixed(2)}`,
	};
};

const mapPublicOrderToTransaction = (data: any, t: any) => {
	if (data.completed_purchase_order?.overview?.items?.length) {
		return mapCompletedPurchaseOrderToTransaction(data.completed_purchase_order, t);
	}

	let totalSum = 0;
	let adminFeeAmount = 0;
	const currency = orderCurrency(data);
	const exchangeRate = numberValue(currency.exchange_rate) || 1;
	const transactions = data.type === 'topup' ? data.overview.transactions.filter((item: any) => ['top_up', 'top_up_cost'].includes(item.type)) : data.overview.transactions;

	const items = transactions.map((item: any) => {
		const description = (() => {
			switch (item.type) {
				case 'top_up':
					return `${currency.prefix} ${currency.name === 'EUR' ? numberValue(item.amount).toFixed(2) : numberValue(item.amount).toFixed(0)} ${currency.name}`;
				case 'top_up_cost':
					adminFeeAmount = numberValue(item.amount);
					return t('transaction.administrative_fee');
				default:
					return t('checkout.item');
			}
		})();

		let itemPrice = 0;
		if (item.type !== 'top_up_cost') {
			totalSum += numberValue(item.amount) * exchangeRate;
			itemPrice = numberValue(item.amount) * exchangeRate;
		} else {
			totalSum += numberValue(item.amount);
			itemPrice = numberValue(item.amount);
		}

		return {
			description: description,
			itemPrice: itemPrice,
			currency: '€',
			quantity: 1,
		};
	});

	return {
		items,
		showTotalPriceString: true,
		totalPriceNumber: totalSum,
		totalDescription: t('transaction.total'),
		totalPriceWithoutFee: totalSum - adminFeeAmount,
		subtitle: t('top_up.success.subtitle'),
		title: `€ ${totalSum}`,
		totalPriceString: `€${totalSum.toFixed(2)}`,
	};
};

const goTo = () => {
	if (tenantConfig?.value?.main_domain) {
		location.href = `https://${tenantConfig?.value?.main_domain}/#/dashboard?clearCart=true`;
	}
};
</script>

<template>
	<ion-content>
		<div v-if="loading || isProcessing" class="flex w-full h-full items-center justify-center px-[34px]">
			<div class="flex flex-col items-center justify-center text-center p-8 bg-white rounded-xl shadow-sm w-full max-w-sm">
				<ion-spinner name="dots" class="mb-4 text-dark-blue"></ion-spinner>
				<h2 class="font-sourcesanspro-semibold text-lg mb-2 text-dark-blue">
					{{ t(pendingPurchase?.status === 'processing' ? 'transaction.booking_processing' : 'transaction.payment_in_progress') }}
				</h2>
				<p class="font-sourcesanspro-regular text-base text-dark-blue/70">
					{{ t('transaction.checking_payment_status') }}
				</p>
			</div>
		</div>

		<div v-else class="flex w-full h-full">
			<div v-if="isFailed" class="flex flex-col w-full h-full justify-between">
				<TopAlert class="mb-8 pt-[75px] pb-12 bg-anykrowd-red">
					<div class="flex flex-col items-center justify-center text-center px-[34px]">
						<ion-icon :icon="closeCircle" class="text-white text-[78px] mb-2" />
						<h1 class="font-sourcesanspro-semibold text-[32px] mb-4">
							{{ t('transaction.booking_failed.title') }}
						</h1>
						<span class="font-sourcesanspro-regular text-m2">
							{{ t(failureSubtitle) }}
						</span>
					</div>
				</TopAlert>

				<div class="flex flex-col w-full px-[34px] align-center mb-[34px]">
					<ion-button class="w-full hover:bg-white hover:rounded-full" expand="full" size="large" shape="round" @click="goTo">
						<div class="flex w-full items-center justify-between">
							{{ t('transaction.continue') }}
							<ion-icon :icon="arrowForwardOutline" class="text-dark-blue mr-2"></ion-icon>
						</div>
					</ion-button>
				</div>
			</div>

			<!-- SUCCESS -->
			<div v-else class="flex flex-col w-full h-full justify-between">
				<TopAlert class="mb-2 pt-[75px] pb-12 bg-anykrowd-green">
					<div class="flex flex-col items-center justify-center">
						<ion-icon :icon="checkmarkCircle" class="text-white text-[78px] mb-2" />
						<h1 class="font-sourcesanspro-semibold text-[40px] mb-4">
							{{ t('checkout.transaction.generic.title') }}
						</h1>
						<span class="font-sourcesanspro-regular text-m2">
							{{ t('checkout.transaction.generic.subtitle') }}
						</span>
					</div>
				</TopAlert>

				<!-- Message box -->
				<div v-if="tenantConfig?.top_up_confirmation_message_title">
					<div class="flex flex-col mx-[34px] p-4 mb-2 bg-white/10 rounded-lg">
						<div class="font-sourcesanspro-semibold text-m2" v-if="tenantConfig?.top_up_confirmation_message_title">
							{{ t(tenantConfig.top_up_confirmation_message_title) }}
						</div>
					</div>
				</div>
				<div class="flex flex-col w-full px-[34px] align-center mb-[34px]">
					<ItemsDetails
						v-if="orderMapped"
						border-class="border-y border-y-slate-200"
						class="mb-4 bg-white rounded-xl"
						text-class="text-dark-blue"
						text-class-two="text-dark-blue/70"
						:title="t('transaction.order_details')"
						:items="
							orderMapped.items.map((item: any) => {
								return {
									itemId: item.itemId,
									title: item.description,
									price: item.unitPrice,
									quantity: item.quantity,
									hideQuantity: item.hideQuantity,
									hidePrice: item.hidePrice,
									totalPrice: item.itemPrice,
									discount: item.discount,
									currency: item.currency,
								};
							})
						"
						:total-title="orderMapped.totalDescription"
						:total-price="orderMapped.totalPriceNumber"
						:total-price-string="orderMapped.totalPriceString || '0'"
						:show-total-price-string="orderMapped.showTotalPriceString" />
					<ion-button class="w-full hover:bg-white hover:rounded-full" expand="full" size="large" shape="round" @click="goTo">
						<div class="flex w-full items-center justify-between">
							{{ t('transaction.continue') }}
							<ion-icon :icon="arrowForwardOutline" class="text-dark-blue mr-2"></ion-icon>
						</div>
					</ion-button>
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
ion-item {
	--background: transparent;
	--inner-padding-end: 0;
	--min-height: 30px;
}

.payconiq {
	background-color: #ff4785;
}
</style>
